/**
 * Question Bank Browser
 * Main interface for viewing and managing questions
 */

import React, { useState, useMemo } from 'react';
import { Search, Plus, Filter, Download, Trash2, Grid3x3, List, Table2, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { QuestionCard } from './QuestionCard';
import { QuestionEditorDialog } from './QuestionEditorDialog';
import { useQuestions } from '../../hooks/useQuestionBank';
import { useQuestionBankStore } from '../../stores/questionBankStore';
import { sortQuestions, filterQuestions, searchQuestions } from '../../utils/api/questionBankApi';
import type { Question } from '../../utils/api/questionBankApi';

interface QuestionBankBrowserProps {
  firmId: string;
  onQuestionClick?: (question: Question) => void;
}

export function QuestionBankBrowser({ firmId, onQuestionClick }: QuestionBankBrowserProps) {
  const { data: allQuestions, isLoading, error } = useQuestions(firmId);
  
  // Store state
  const {
    searchQuery,
    setSearchQuery,
    filters,
    updateFilter,
    clearFilters,
    sortField,
    sortDirection,
    setSortField,
    toggleSortDirection,
    viewMode,
    setViewMode,
    selectedQuestionIds,
    toggleQuestionSelection,
    selectAllQuestions,
    clearSelection,
    openQuestionEditor,
    isQuestionEditorOpen,
    editingQuestionId,
    closeQuestionEditor,
  } = useQuestionBankStore();

  // Filter, search, and sort questions
  const displayedQuestions = useMemo(() => {
    if (!allQuestions) return [];
    
    let result = allQuestions;

    // Apply filters
    if (Object.keys(filters).length > 0) {
      result = filterQuestions(result, filters);
    }

    // Apply search
    if (searchQuery) {
      result = searchQuestions(result, searchQuery);
    }

    // Apply sort
    result = sortQuestions(result, sortField, sortDirection);

    return result;
  }, [allQuestions, filters, searchQuery, sortField, sortDirection]);

  // Get unique categories for filter
  const categories = useMemo(() => {
    if (!allQuestions) return [];
    return Array.from(new Set(allQuestions.map(q => q.category))).sort();
  }, [allQuestions]);

  // Get unique tags for filter
  const allTags = useMemo(() => {
    if (!allQuestions) return [];
    const tags = new Set<string>();
    allQuestions.forEach(q => q.tags.forEach(tag => tags.add(tag)));
    return Array.from(tags).sort();
  }, [allQuestions]);

  // Bulk actions
  const handleSelectAll = () => {
    if (selectedQuestionIds.length === displayedQuestions.length) {
      clearSelection();
    } else {
      selectAllQuestions(displayedQuestions.map(q => q.id));
    }
  };

  const handleBulkDelete = () => {
    if (window.confirm(`Delete ${selectedQuestionIds.length} selected questions?`)) {
      // TODO: Implement bulk delete
      console.log('Bulk delete:', selectedQuestionIds);
    }
  };

  const handleExport = () => {
    const data = JSON.stringify(displayedQuestions, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `questions-${new Date().toISOString()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Active filter count
  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading questions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <p className="text-destructive">Error loading questions: {error.message}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold">Question Bank</h1>
          <p className="text-muted-foreground">
            {allQuestions?.length || 0} questions
            {displayedQuestions.length !== allQuestions?.length && 
              ` • ${displayedQuestions.length} filtered`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={handleExport} variant="outline" size="sm">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          <Button onClick={() => openQuestionEditor()} size="sm">
            <Plus className="w-4 h-4 mr-2" />
            New Question
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col gap-4 mb-6">
        {/* Search Bar */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search questions by text, ID, category, or tags..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2"
              >
                <X className="w-4 h-4 text-muted-foreground hover:text-foreground" />
              </button>
            )}
          </div>
          
          {/* View Mode Toggle */}
          <div className="flex border rounded-md">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('grid')}
              className="rounded-r-none"
            >
              <Grid3x3 className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              className="rounded-none"
            >
              <List className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'table' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('table')}
              className="rounded-l-none"
            >
              <Table2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2">
          <Select
            value={filters.category || 'all'}
            onValueChange={(value) => updateFilter('category', value === 'all' ? undefined : value)}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(cat => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={filters.phase || 'all'}
            onValueChange={(value) => updateFilter('phase', value === 'all' ? undefined : value as any)}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Phase" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Phases</SelectItem>
              <SelectItem value="discovery">Discovery</SelectItem>
              <SelectItem value="scoping">Scoping</SelectItem>
              <SelectItem value="implementation">Implementation</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={filters.source || 'all'}
            onValueChange={(value) => updateFilter('source', value === 'all' ? undefined : value as any)}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Source" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sources</SelectItem>
              <SelectItem value="pathkit_default">PathKit Default</SelectItem>
              <SelectItem value="consultant_custom">Custom</SelectItem>
              <SelectItem value="imported">Imported</SelectItem>
            </SelectContent>
          </Select>

          {activeFilterCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="text-muted-foreground"
            >
              <X className="w-4 h-4 mr-1" />
              Clear Filters ({activeFilterCount})
            </Button>
          )}
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectedQuestionIds.length > 0 && (
        <div className="flex items-center justify-between p-4 mb-4 bg-muted rounded-lg">
          <div className="flex items-center gap-4">
            <Checkbox
              checked={selectedQuestionIds.length === displayedQuestions.length}
              onCheckedChange={handleSelectAll}
            />
            <span className="text-sm font-medium">
              {selectedQuestionIds.length} selected
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="destructive"
              size="sm"
              onClick={handleBulkDelete}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Selected
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={clearSelection}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Questions Grid/List */}
      {displayedQuestions.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <p className="text-lg font-medium text-muted-foreground mb-2">No questions found</p>
          <p className="text-sm text-muted-foreground mb-4">
            {searchQuery || activeFilterCount > 0
              ? 'Try adjusting your search or filters'
              : 'Get started by creating your first question'}
          </p>
          {!searchQuery && activeFilterCount === 0 && (
            <Button onClick={() => openQuestionEditor()}>
              <Plus className="w-4 h-4 mr-2" />
              Create Question
            </Button>
          )}
        </div>
      ) : (
        <div className={
          viewMode === 'grid'
            ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'
            : viewMode === 'list'
            ? 'flex flex-col gap-2'
            : 'overflow-x-auto'
        }>
          {displayedQuestions.map(question => (
            <QuestionCard
              key={question.id}
              question={question}
              viewMode={viewMode}
              isSelected={selectedQuestionIds.includes(question.id)}
              onSelect={() => toggleQuestionSelection(question.id)}
              onClick={() => onQuestionClick?.(question) || openQuestionEditor(question.id)}
            />
          ))}
        </div>
      )}
      
      {/* Question Editor Dialog */}
      <QuestionEditorDialog
        firmId={firmId}
      />
    </div>
  );
}